import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Youtube, Sparkles, ShieldCheck, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export default function About() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "About — AI Pathshaalaa";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-24">
        <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl">
          The best AI teachers are already on YouTube. <span className="gradient-text">We just put them in order.</span>
        </h1>
        <div className="mt-8 space-y-5 text-base text-muted-foreground leading-relaxed">
          <p>
            <strong className="text-foreground">AI Pathshaalaa</strong> exists because learning AI in 2026 is overwhelming.
            There are millions of free tutorials, but no clear order — so most people bounce between random videos,
            feel lost, and give up. We call it tutorial hell.
          </p>
          <p>
            We fix it with curation. Our engine searches YouTube through the official API, judges the <em>teaching quality</em> of
            every video (not the view count), and sequences the best free lessons into one personalized course — matched to your
            level, profession, goal and language (English, Hindi or Hinglish).
          </p>
          <p>
            Your path stays alive: every day we scan for new videos, and when something better is published for a topic
            you're learning, it flows into your roadmap automatically — with a notification so you never miss it.
          </p>
        </div>

        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {[
            { icon: Youtube, title: "Videos stay free", text: "Everything plays in the official YouTube player. Creators keep their views and ads." },
            { icon: Sparkles, title: "We charge for curation", text: "Your subscription pays for path-building, AI summaries, quizzes and daily freshness — never for the videos." },
            { icon: ShieldCheck, title: "Compliance first", text: "Official YouTube API, full attribution, and honest data practices." },
          ].map((c) => (
            <div key={c.title} className="rounded-2xl border border-border bg-card p-5">
              <c.icon className="h-5 w-5 text-primary" />
              <h2 className="mt-3 font-heading font-bold">{c.title}</h2>
              <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{c.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex items-center gap-4 flex-wrap">
          <Button size="lg" data-testid="about-cta" onClick={() => navigate("/quiz")} className="rounded-full px-7">
            Build my AI path — free <ArrowRight className="h-4 w-4" />
          </Button>
          <Link to="/paths" className="text-sm font-semibold text-primary hover:underline underline-offset-4">
            Browse learning paths →
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
